<template>
  <basic-dialog :show="show" @close="$emit('close')" max-width="99%" draggable>
    <template v-slot:title>Batch Receipts Create</template>
    <v-row>
      <v-col cols="6">
        <v-text-field
          label="Lodgement Ref"
          v-model="lodgementRef"
          :outlined="outlinedInputs"
          hide-details
          dense
        ></v-text-field>
      </v-col>
      <v-col>
        <payment-type-select
          v-model="paymentType"
          :outlined="outlinedInputs"
          hide-details
          dense
        ></payment-type-select>
      </v-col>
      <v-col>
        <date-picker
          customLabel="Lodgement Date"
          @onDateSelected="onDateSelected"
          :gtDateString="lodgementDate"
          :outlined="outlinedInputs"
          hide-details
          dense
        ></date-picker>
      </v-col>
      <v-col>
        <v-autocomplete
          label="Currency"
          :outlined="outlinedInputs"
          v-model="currencyCode"
          :items="currencyList"
          @change="onCurrencyChange"
          hide-details
          dense
        ></v-autocomplete>
      </v-col>
      <v-col>
        <v-text-field
          label="Exchange Rate"
          v-model:number="exchangeRate"
          :outlined="outlinedInputs"
          hide-details
          dense
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-text-field
          label="Payer Name"
          v-model="payerName"
          :outlined="outlinedInputs"
          hide-details
          dense
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          label="Payer Surname"
          v-model="payerSurname"
          :outlined="outlinedInputs"
          hide-details
          dense
        ></v-text-field>
      </v-col>
      <v-col v-if="showPaymentMethods">
        <payment-method-list-select
          v-model="paymentMethodId"
          :outlined="outlinedInputs"
          hide-details
          dense
        ></payment-method-list-select>
      </v-col>
      <v-col>
        <v-text-field
          label="Notes"
          v-model="notes"
          :outlined="outlinedInputs"
          hide-details
          dense
        ></v-text-field>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-divider></v-divider>
      </v-col>
    </v-row>
    <v-card>
      <v-data-table
        :items="items"
        item-key="id"
        show-select
        v-model="selected"
        :headers="headers"
        @item-selected="onItemSelect"
      >
        <template v-slot:item.amount="{ item }">
          <v-text-field
            type="number"
            v-model="item.amount"
            min="0"
            class="pt-1 ma-0"
            style="min-width: 120px"
            @change="amountChanged(item)"
          ></v-text-field>
        </template>
        <template v-slot:item.clientName="{ item }">
          <client-label
            v-model="item.clientName"
            :client-id="item.client ? item.client.id : ''"
          ></client-label>
        </template>
        <template v-slot:item.reference="{ item }">
          <reference-label
            v-model="item.reference"
            :itinerary-id="item.id"
          ></reference-label>
        </template>
        <template v-slot:item.agentName="{ item }">
          <agent-label
            v-model="item.agentName"
            :agent-id="item.agentId"
          ></agent-label>
        </template>
        <template v-slot:item.bookingStatus="{ item }">
          <status-label v-model="item.bookingStatus"></status-label>
        </template>
        <template v-slot:item.startDate="{ item }">{{
          item.startDate | formatDate
        }}</template>
        <template v-slot:item.endDate="{ item }">{{
          item.endDate | formatDate
        }}</template>
        <template v-slot:item.createdAt="{ item }">{{
          item.createdAt | formatDate
        }}</template>
        <template v-slot:item.totalPrice="{ item }">{{
          item.totalPrice | priceLabeler
        }}</template>
        <template v-slot:item.paid="{ item }">{{
          item.paid | priceLabeler
        }}</template>
        <template v-slot:item.balance="{ item }">{{
          item.balance | priceLabeler
        }}</template>
        <template v-slot:item.commission="{ item }">{{
          item.commission | priceLabeler
        }}</template>
        <template v-slot:item.commissionAdjustment="{ item }">{{
          item.commissionAdjustment | priceLabeler
        }}</template>
        <template v-slot:item.netCommission="{ item }">{{
          item.netCommission | priceLabeler
        }}</template>
        <template v-slot:item.profit.netProfit="{ item }">{{
          item.profit.netProfit | priceLabeler
        }}</template>
        <template v-slot:item.profit.grossProfit="{ item }">{{
          item.profit.grossProfit | priceLabeler
        }}</template>
        <template v-slot:item.grossBalance="{ item }">{{
          item.grossBalance | priceLabeler
        }}</template>
        <template v-slot:item.isLocked="{ item }">
          <v-simple-checkbox
            v-model="item.isLocked"
            disabled
          ></v-simple-checkbox>
        </template>

        <template v-slot:footer>
          <v-layout justify-space-around>
            <span class="py-1">
              Total:
              <strong>{{ total }}</strong>
            </span>
          </v-layout>
        </template>
      </v-data-table></v-card
    >
    <template v-slot:actions>
      <v-btn
        v-if="$store.getters.hasAccessTo('finances:xero_export')"
        @click="onCreateAndExportToXero"
        :disabled="!isValid || loading"
        :loading="loading"
        >Create &amp; Export to Xero</v-btn
      >
      <v-btn
        color="primary"
        :disabled="!isValid || loading"
        @click="onCreate(false)"
        :loading="loading"
        >Create</v-btn
      >
    </template>
    <div v-if="debug">
      <code>{{ items.map((item) => item.amount) }}</code>
    </div>
  </basic-dialog>
</template>

<script>
import BasicDialog from "../../shared/BasicDialog.vue";
import DatePicker from "../../shared/DatePicker.vue";
import PaymentTypeSelect from "../../shared/PaymentTypeSelect.vue";
import TaskStatusMixin from "../../../../mixins/TaskStatusMixin";
import ReferenceLabel from "../../shared/labels/ReferenceLabel";
import StatusLabel from "../../shared/labels/StatusLabel";
import AgentLabel from "../../shared/labels/AgentLabel";
import ClientLabel from "../../shared/labels/ClientLabel";
import PaymentMethodListSelect from "../../shared/PaymentMethodListSelect.vue";
import gql from "graphql-tag";
const CREATE_ITINERARY_PAYMENTS = gql`
  mutation ($input: ItineraryPaymentsInput) {
    createItineraryPayments(input: $input) {
      id
    }
  }
`;
import { ALL_CURRENCIES_SEARCH_QUERY } from "../../../../constants/graphql";
export default {
  name: "batch-create-payments-dialog",

  props: {
    show: {
      type: Boolean,
      default: () => false,
    },
    itineraries: {
      type: Array,
      default: () => [],
    },
    columns: {
      type: Array,
      default: () => [],
    },
  },

  mixins: [TaskStatusMixin],
  components: {
    BasicDialog,
    DatePicker,
    PaymentTypeSelect,
    ReferenceLabel,
    StatusLabel,
    AgentLabel,
    ClientLabel,
    PaymentMethodListSelect,
  },

  data() {
    return {
      debug: false,
      items:
        this.itineraries.length > 0
          ? [
              ...this.itineraries.map((item) => ({
                ...item,
                amount: Number(item.balance).toFixed(2),
              })),
            ]
          : [],
      selected:
        this.itineraries.length > 0
          ? [
              ...this.itineraries.map((item) => ({
                ...item,
                amount: Number(item.balance).toFixed(2),
              })),
            ]
          : [],
      lodgementRef: "",
      lodgementDate: new Date().toISOString(false),
      currencyCode: "",
      exchangeRate: 1.0,
      payerName: "",
      payerSurname: "",
      paymentType: "",
      notes: "",
      loading: false,
      paymentMethodId: "",
    };
  },

  computed: {
    showPaymentMethods() {
      return this.$store.getters.hasAccessTo("finances:payment_methods");
    },
    isValid() {
      return (
        this.selected.length > 0 &&
        this.currencyCode != "" &&
        this.paymentType != "" &&
        this.lodgementRef != ""
      );
    },
    // items() {
    //   return this.itineraries.map((iti) => ({ ...iti, amount: iti.balance }));
    // },
    currencyList() {
      const list = this.itineraries.reduce((l, item) => {
        if (l.indexOf(item.currencyCode) == -1) {
          l.push(item.currencyCode);
        }
        return l;
      }, []);
      return list;
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    headers() {
      const amountCol = {
        text: "Receipt Amount",
        value: "amount",
        name: "paymentAmount",
      };
      return [...this.columns.filter((c) => c.name != "actions"), amountCol];
    },
    total() {
      const selectedIds = this.selected.map((i) => i.id);
      return this.items
        .filter((item) => selectedIds.indexOf(item.id) > -1)
        .reduce((t, item) => t + Number(item.amount), 0)
        .toFixed(2);
    },
  },

  watch: {
    itineraries(newVal) {
      this.items = [
        ...newVal.map((item) => ({
          ...item,
          amount: Number(item.balance).toFixed(2),
        })),
      ];
      this.selected = [...this.items];
      if (this.currencyList.length == 1) {
        this.currencyCode = this.currencyList[0];
      }
    },
    currencyCode(newVal) {
      this.onCurrencyChange(newVal);
    },
    selected(newVal) {},
  },

  methods: {
    onItemSelect({ item, value }) {
      //console.log(item);
      const editedIndex = this.items.findIndex((i) => i.id == item.id);
      if (editedIndex) {
        Object.assign(this.items[editedIndex], {
          ...item,
          amount: value ? Number(item.balance) : null,
        });
      }
    },
    onCurrencyChange(payload) {
      if (this.currencies && this.currencies.edges) {
        const currency = this.currencies.edges.find(
          (c) => c.node.code == payload
        );
        if (currency) {
          this.exchangeRate = currency.node.exchangeRate;
        }
      }
    },
    onDateSelected(payload) {
      this.lodgementDate = payload;
    },
    taskStatusCompleteHandler(data) {
      //console.log("task completed: ", data);
      this.loading = false;
      this.$store.dispatch("showAppSnackbar", {
        msg: data.taskStatus.notes,
      });
      this.$emit("complete");
      this.reset();
    },
    reset() {
      this.lodgementRef = "";
      this.notes = "";
      this.payerName = "";
      this.payerSurname = "";
    },
    onCreateAndExportToXero() {
      this.onCreate(true);
    },
    onCreate(exportToXero = false) {
      const input = {
        lodgementRef: this.lodgementRef,
        notes: this.notes,
        lodgementDate: this.lodgementDate,
        paymentType: this.paymentType,
        paymentMethodId: this.paymentMethodId,
        payerName: this.payerName,
        payerSurname: this.payerSurname,
        currencyCode: this.currencyCode,
        exchangeRate: Number(this.exchangeRate),
        totalAmount: Number(this.total),
        itineraries: this.selected.map((item) => ({
          id: item.id,
          amount: Number(item.amount),
        })),
        exportToXero: exportToXero,
      };
      //console.log(input);
      this.$apollo
        .mutate({
          mutation: CREATE_ITINERARY_PAYMENTS,
          variables: { input },
        })
        .then(({ data }) => {
          //console.log(data.createSupplierPayments.id);
          this.loading = true;
          this.taskID = data.createItineraryPayments.id;
          this.checkStatus = true;
        })
        .catch((err) => {
          this.loading = false;
          console.error(err);
        });
    },
    amountChanged(item) {
      item.amount = Number(item.amount).toFixed(2);
    },
  },

  apollo: {
    currencies: {
      query: ALL_CURRENCIES_SEARCH_QUERY,
      variables() {
        return {
          includeExchangeRate: true,
        };
      },
      skip() {
        return !this.show;
      },
      result() {
        if (this.currencyCode != "") {
          this.onCurrencyChange(this.currencyCode);
        }
      },
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/finances/payments/BatchCreatePaymentsDialog.vue